import {gql, useLazyQuery} from '@apollo/client';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import {Spinner} from 'evergreen-ui';
import {useApplicationIsInternalAuth} from 'common/component/ApplicationRootContext';
import {getCommunicationStatusColorClass} from 'offer/component/conversation';
import type {OfferConversationState} from 'offer/component/conversation';
import {useReferralConversationSummaries} from 'offer/hook/useSharedMessaging';
import {OfferConversationSummary, OfferStatus} from 'placement/domains';
import React, {lazy, Suspense, useCallback, useEffect, useMemo, useState} from 'react';
import {useToken} from 'security';
import {createPortal} from 'react-dom';
import { ChatIcon } from 'offer/component/conversation/ConversationParts';
import { FeatureFlagSwitch, ProviderPortalFeatureFlag } from 'featureflag';

const OfferConversationPopup = lazy(() => import('offer/component/conversation').then((module) => ({default: module.OfferConversationPopup})));

export const REFERRAL_COMMUNICATION_OFFERS_QUERY = gql`
    query referralCommunicationOffers($referralGuids: [String]!) {
        offersByReferrals(referralGuids: $referralGuids) {
            guid
            status
            provider {
                name
            }
        }
    }
`;

type CommunicationOffer = {
    guid: string;
    status: string;
    provider: {
        name: string;
    };
};

type ReferralCommunicationsPopupProps = {
    referralGuid: string;
    summaries: OfferConversationSummary[];
    readOnly: boolean;
};

const toState = (summary?: OfferConversationSummary): OfferConversationState => ({
    urgent: Boolean(summary?.urgent),
    hasConversation: Boolean(summary?.hasConversation),
    unreadCount: summary?.clientPlacementTeamUnreadCount ?? 0,
});

export const ReferralCommunicationsPopup = ({
    referralGuid,
    summaries,
    readOnly,
}: ReferralCommunicationsPopupProps) => {
    const [pickerOpen, setPickerOpen] = useState(false);
    const [selectedOffer, setSelectedOffer] = useState<CommunicationOffer | null>(null);
    const [states, setStates] = useState<Record<string, OfferConversationState>>(() =>
        Object.fromEntries(summaries.map((summary) => [summary.offerGuid, toState(summary)]))
    );
    const token = useToken();
    const isInternalAuth = useApplicationIsInternalAuth();
    const liveSummaries = useReferralConversationSummaries(referralGuid, token, isInternalAuth);
    const [loadOffers, {data, loading}] = useLazyQuery<{offersByReferrals: CommunicationOffer[]}>(
        REFERRAL_COMMUNICATION_OFFERS_QUERY,
        {fetchPolicy: 'no-cache'}
    );

    useEffect(() => {
        setStates(Object.fromEntries(summaries.map((summary) => [summary.offerGuid, toState(summary)])));
    }, [summaries]);

    useEffect(() => {
        if (!liveSummaries || liveSummaries.length === 0) {
            return;
        }
        setStates((current) => {
            const next = {...current};
            liveSummaries.forEach((summary) => {
                next[summary.offerGuid] = toState(summary);
            });
            return next;
        });
    }, [liveSummaries]);

    const summaryByOfferGuid = useMemo(() => new Map(
        summaries.map((summary) => [summary.offerGuid, summary])
    ), [summaries]);
    const aggregateState = Object.values(states).reduce((aggregate, state) => ({
        urgent: aggregate.urgent || state.urgent,
        unreadCount: aggregate.unreadCount + state.unreadCount,
        hasConversation: aggregate.hasConversation || state.hasConversation,
    }), {urgent: false, unreadCount: 0, hasConversation: false});
    const triggerColorClass = getCommunicationStatusColorClass(aggregateState);
    const selectedState = selectedOffer ? states[selectedOffer.guid] : undefined;
    const selectedSummary = selectedOffer && selectedState ? {
        offerGuid: selectedOffer.guid,
        urgent: selectedState.urgent,
        hasConversation: selectedState.hasConversation,
        providerUnreadCount: summaryByOfferGuid.get(selectedOffer.guid)?.providerUnreadCount ?? 0,
        clientPlacementTeamUnreadCount: selectedState.unreadCount,
    } : undefined;
    const offers = (data?.offersByReferrals ?? [])
        .filter((offer) => readOnly
            ? summaryByOfferGuid.has(offer.guid)
            : offer.status === OfferStatus.SUBMITTED)
        .sort((left, right) => left.provider.name.localeCompare(right.provider.name));

    const openPicker = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setPickerOpen(true);
        loadOffers({variables: {referralGuids: [referralGuid]}});
    };
    const close = () => {
        setSelectedOffer(null);
        setPickerOpen(false);
    };
    const handleStateChange = useCallback((state: OfferConversationState) => {
        if (!selectedOffer) return;
        setStates((current) => ({...current, [selectedOffer.guid]: state}));
    }, [selectedOffer]);

    return (
        <>
            <button
                type="button"
                onClick={openPicker}
                className={`inline-flex min-w-12 items-center justify-center gap-1 rounded px-2 py-1 transition-colors focus:outline-none ${triggerColorClass}`}
                aria-label={`${aggregateState.unreadCount} unread referral communications`}
                title="Referral communications"
            >
                <ChatIcon/>
                {aggregateState.unreadCount > 0 && <span className="text-sm font-semibold">
                    {aggregateState.unreadCount}
                </span>}
            </button>
            {pickerOpen && !selectedOffer && createPortal(
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-25 p-6"
                    role="presentation"
                    onClick={(event) => event.stopPropagation()}
                >
                    <div
                        className="w-96 max-w-full rounded-lg bg-white shadow-xl"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="provider-conversation-picker-title"
                    >
                        <div className="flex items-center justify-between rounded-t-lg bg-blue-600 p-4 text-white">
                            <h3 id="provider-conversation-picker-title" className="font-semibold">Provider Communications</h3>
                            <button type="button" onClick={close} className="text-xl leading-none hover:text-gray-200 focus:outline-none" aria-label="Close provider communications">
                                &times;
                            </button>
                        </div>
                        <div className="max-h-96 overflow-y-auto p-3">
                            {loading && <div className="flex justify-center py-6"><Spinner size={24}/></div>}
                            {!loading && offers.length === 0 && (
                                <div className="py-6 text-center text-sm text-gray-500">No provider conversations are available.</div>
                            )}
                            {!loading && offers.map((offer) => {
                                const state = states[offer.guid] ?? toState();
                                const colorClass = getCommunicationStatusColorClass(state, 'list');
                                return (
                                    <button
                                        key={offer.guid}
                                        type="button"
                                        onClick={() => setSelectedOffer(offer)}
                                        className="flex w-full items-center justify-between rounded px-3 py-3 text-left hover:bg-gray-100 focus:outline-none"
                                    >
                                        <span className="font-medium text-gray-800">{offer.provider.name}</span>
                                        <span className={`inline-flex items-center gap-1 px-2 py-1 ${colorClass}`}>
                                            <ChatIcon/>
                                            {state.unreadCount > 0 && <span className="text-sm font-semibold">{state.unreadCount}</span>}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>,
                document.body
            )}
            {selectedOffer && (
                <Suspense fallback={null}>
                    <FeatureFlagSwitch flag={ProviderPortalFeatureFlag.BHPNPP_3077_IS_TWO_WAY_MESSAGING_DISABLED} negate>
                        <OfferConversationPopup
                            offerGuid={selectedOffer.guid}
                            conversationSummary={selectedSummary}
                            readOnly={readOnly}
                            open
                            hideTrigger
                            onBack={offers.length > 1 ? () => setSelectedOffer(null) : undefined}
                            onClose={close}
                            onStateChange={handleStateChange}
                        />
                    </FeatureFlagSwitch>
                </Suspense>
            )}
        </>
    );
};
